import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../auth';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';

import { users, customers } from '../../services/dataClient';
import '../../styles/design-tokens.css';

const isDev = process.env.NODE_ENV === 'development' || process.env.REACT_APP_SHOW_DEV_TOOLS === 'true';

// Resolve customerType ('Reseller' | 'End-Customer' | null) for a user
function getCustomerType(user) {
  if (!user?.customerName) return null;
  return customers.find((c) => c.name === user.customerName)?.customerType ?? null;
}

// Small pill badge for Reseller / End-Customer
function CustomerTypeBadge({ type }) {
  if (!type) return null;
  const isReseller = type === 'Reseller';
  return (
    <Box
      sx={{ display: 'inline-flex',
        alignItems: 'center',
        px: '6px',
        py: '1px',
        borderRadius: '4px',
        backgroundColor: isReseller ? '#E0F2F1' : '#EDE7F6',
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{ fontSize: '0.625rem',
          fontWeight: 700,
          letterSpacing: '0.03em',
          color: isReseller ? '#00695C' : '#512DA8',
          lineHeight: 1.4,
          textTransform: 'uppercase',
        }}
      >
        {isReseller ? 'Reseller' : 'End-Customer'}
      </Typography>
    </Box>
  );
}

// Inline dev user switcher — rendered inside TopBar right-side group
function InlineDevSwitcher() {
  const { currentUser, setMockUser } = useAuth();
  const location = useLocation();

  const handleChange = (userId) => {
    if (userId === currentUser.id) return;
    setMockUser(userId);
    const selected = users.find((u) => u.id === userId);
    if (selected?.role === 'Customer') {
      window.location.href = '/portal/tickets';
    } else if (location.pathname.startsWith('/portal')) {
      window.location.href = '/';
    } else {
      window.location.reload();
    }
  };

  const currentType = getCustomerType(currentUser);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      {/* DEV label */}
      <Typography
        sx={{ fontSize: '0.625rem',
          fontWeight: 700,
          color: '#009688',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          lineHeight: 1,
          border: '1px solid #B2DFDB',
          borderRadius: '4px',
          px: '5px',
          py: '2px',
          flexShrink: 0,
        }}
      >
        DEV
      </Typography>

      {/* Customer type badge for current user */}
      <CustomerTypeBadge type={currentType} />

      <Select
        size="small"
        value={currentUser.id}
        onChange={(e) => handleChange(e.target.value)}
        sx={{ fontSize: '0.8125rem',
          minWidth: 210,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--grey-200)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--grey-400)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#009688' },
          '& .MuiSelect-select': {
            py: 0,
            pr: '32px !important',
            display: 'flex',
            alignItems: 'center',
            minHeight: '32px',
          },
        }}
        renderValue={(id) => {
          const u = users.find((u) => u.id === id);
          return (
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-900)', fontWeight: 500, lineHeight: 1 }}>
              {u?.name ?? '—'}
            </Typography>
          );
        }}
      >
        {users.map((u) => {
          const type = getCustomerType(u);
          return (
            <MenuItem
              key={u.id}
              value={u.id}
              sx={{ fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '8px', py: '8px' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--grey-900)', lineHeight: 1.2 }}>
                  {u.name}
                </Typography>
                <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-500)', lineHeight: 1.3, mt: '2px' }}>
                  {u.role}{u.customerName ? ` · ${u.customerName}` : ''}
                </Typography>
              </Box>
              <CustomerTypeBadge type={type} />
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
}

export default function TopBar({ onToggle }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const { currentUser, logout } = useAuth();

  const handleOpenMenu = (e) => setMenuAnchor(e.currentTarget);
  const handleCloseMenu = () => setMenuAnchor(null);

  return (
    <Box
      component="header"
      sx={{ position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        height: 88,
        backgroundColor: 'var(--paper)',
        borderBottom: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pl: '21px',
        pr: '21px',
        gap: '8px',
      }}
    >
      {/* Left group: logo + hamburger */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '30px', color: '#121926', lineHeight: 1 }}>
            CoverIT
          </Typography>
          <Typography sx={{ fontWeight: 400, fontSize: '22px', letterSpacing: '0.5px', color: '#009688', lineHeight: 1, ml: '6px' }}>
            LogisticsHub
          </Typography>
        </Box>

        <IconButton
          onClick={onToggle}
          sx={{ p: '6px',
            color: '#00897B',
            backgroundColor: '#E0F2F1',
            borderRadius: '8px',
            '&:hover': { backgroundColor: '#00897B', borderRadius: '8px', color: '#ffffff' },
          }}
        >
          <MenuIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Right group: dev switcher + gear */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {isDev && <InlineDevSwitcher />}

        <IconButton
          onClick={handleOpenMenu}
          sx={{ width: 44,
            height: 44,
            backgroundColor: '#ECEFF1',
            borderRadius: '50%',
            color: '#607d8b',
            '&:hover': { backgroundColor: '#607D8B', color: '#ffffff' },
          }}
        >
          <SettingsIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>

      {/* User menu popover */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '12px',
              minWidth: 332,
              width: 332,
              boxShadow: '0px 4px 20px rgba(0,0,0,0.12)',
              mt: '8px',
            },
          },
        }}
      >
        <Box sx={{ px: '20px', py: '18px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#121926', lineHeight: 1.3 }}>
            {currentUser?.name ?? 'Logistics User'}
          </Typography>
        </Box>

        <Divider />

        <MenuItem
          onClick={() => {
            handleCloseMenu();
            logout();
            window.location.href = '/login';
          }}
          sx={{ px: '20px', py: '14px', gap: '12px', '&:hover': { backgroundColor: '#E0F2F1' } }}
        >
          <LogoutIcon sx={{ fontSize: 18, color: '#697586' }} />
          <Typography sx={{ fontSize: '15px', color: '#364152' }}>
            Logout
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}
